import axiosInstance from './axiosInstance'

// ── Entity types ──────────────────────────────────────────────────────────────

export type EmailEntityType = 'LEAD' | 'CONTACT' | 'CUSTOMER' | 'DEAL' | 'RETENTION'

// ── Payload sent when composing an email ─────────────────────────────────────

export interface AttachmentMeta {
  name: string
  sizeBytes: number
  contentType: string
  url?: string
}

export interface SendEmailPayload {
  entityType?: EmailEntityType
  entityId?: string
  to: string[]
  cc?: string[]
  bcc?: string[]
  subject: string
  htmlBody: string
  templateName?: string
  /** Actual File objects to attach to the email. */
  files?: File[]
}

// ── Email log record returned from the API ────────────────────────────────────

export interface EmailLog {
  id: string
  entityType: EmailEntityType | null
  entityId: string | null
  status: 'SENT' | 'DRAFT' | 'FAILED'
  subject: string
  to: string[]
  cc: string[]
  bcc: string[]
  attachments: AttachmentMeta[]
  htmlBody: string
  templateName: string | null
  sentAt: string | null
  createdAt: string
  sentByName: string | null
}

export interface PaginatedEmailLogs {
  items: EmailLog[]
  total: number
  page: number
  limit: number
  totalPages: number
}

export interface EmailLogFilters {
  search?: string
  statuses?: string[]
  ownerIds?: string[]
  from?: string
  to?: string
  // SuperAdmin / Org Admin: narrow by org / LOB (LOB derived from the linked entity)
  orgIds?: string[]
  lobIds?: string[]
}

// ── API methods ───────────────────────────────────────────────────────────────

export const emailApi = {
  /**
   * Sends an email via multipart/form-data so actual file attachments
   * are carried to the backend and forwarded to Azure Communication Services.
   */
  send: async (payload: SendEmailPayload): Promise<void> => {
    const form = new FormData()

    if (payload.entityType) form.append('entityType', payload.entityType)
    if (payload.entityId) form.append('entityId', payload.entityId)

    payload.to.forEach(addr => form.append('to', addr))
    payload.cc?.forEach(addr => form.append('cc', addr))
    payload.bcc?.forEach(addr => form.append('bcc', addr))

    form.append('subject', payload.subject)
    form.append('htmlBody', payload.htmlBody)

    if (payload.templateName) form.append('templateName', payload.templateName)

    // Append actual file objects — axios will set the correct content-type boundary
    payload.files?.forEach(file => form.append('files', file, file.name))

    await axiosInstance.post('/emails/send', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      _suppressToast: true,
    } as any)
  },

  /**
   * Fetch email history for any CRM entity.
   * @param entityType  LEAD | CONTACT | CUSTOMER | DEAL
   * @param entityId    UUID of the entity
   */
  getByEntity: async (entityType: EmailEntityType, entityId: string): Promise<EmailLog[]> => {
    const res = await axiosInstance.get<{ data: EmailLog[] }>(
      `/emails/${entityType}/${entityId}`
    )
    return res.data.data ?? []
  },

  /**
   * Fetch all email logs for the activity feed.
   */
  getAllForActivity: async (): Promise<EmailLog[]> => {
    const res = await axiosInstance.get<{ data: EmailLog[] }>('/emails')
    return res.data.data ?? []
  },

  /**
   * Fetch paginated email logs for the activity feed.
   */
  getPagedForActivity: async (page: number, pageSize: number, filters?: EmailLogFilters): Promise<PaginatedEmailLogs> => {
    const res = await axiosInstance.get<{ data: PaginatedEmailLogs }>('/emails/paged', {
      params: { page, pageSize, ...filters }
    })
    return res.data.data
  },
}
